import { FileText } from 'lucide-react';
import { SpinningLoader } from 'risa-oasis-ui_v2';
import type { ExtractedDocument } from '../../../types';
import { Markdown } from '../../../components/Markdown/Markdown';
import { formatDisplayDate } from '../../../utils/displayDate';
import './PerDocumentDiffView.scss';

interface PerDocumentDiffViewProps {
  documents?: ExtractedDocument[];
  narrative?: string;
  /** True while the structured summary is still being fetched. */
  isLoading?: boolean;
  /** Whether the patient is a cancer MRN. Non-cancer MRNs are not summarised, so the
   *  empty state points the CRC to "View Source" instead of "no extraction". */
  isCancer?: boolean;
  /**
   * Top-level extraction keys to drop before rendering. Used when this view is shown as an
   * "Additional details" panel ALONGSIDE a structured view: the structured view already
   * renders these, so excluding them leaves only the cancer-specific extras.
   */
  excludeKeys?: string[];
  /** Hide the trend/comparison narrative (the structured view already owns it). */
  hideNarrative?: boolean;
  /** Card title (defaults to "Report summary"). */
  title?: string;
}

/** Remove the given top-level keys from a document's extraction data. */
function stripKeys(
  data: Record<string, unknown> | undefined,
  excludeKeys?: string[],
): Record<string, unknown> {
  if (!data) return {};
  if (!excludeKeys?.length) return data;
  const drop = new Set(excludeKeys);
  return Object.fromEntries(Object.entries(data).filter(([key]) => !drop.has(key)));
}

/** A single flattened leaf field pulled out of a document's extraction. */
interface FlatField {
  section: string;
  label: string;
  path: string;
  value: string;
}

/** A scalar-only object row inside a tabular array (e.g. one lab result). */
type ScalarRow = Record<string, string | number | boolean | null>;

/**
 * A repeated array-of-objects (e.g. a CBC panel) rendered as a proper table
 * rather than being flattened into `#1 › Field` label/value pairs.
 */
interface TableSection {
  name: string;
  columns: string[];
  rows: ScalarRow[];
}

/**
 * Format a document date while preserving the report's empty placeholder.
 *
 * @param value - Document date to format.
 * @returns The `MM-DD-YYYY` date or an em dash.
 */
function formatDate(value?: string): string {
  return formatDisplayDate(value, '—');
}

/**
 * Humanize a schema key segment (snake_case / camelCase / numeric index) into a
 * readable label. Numeric segments become "#n" so array items read naturally.
 *
 * @param segment - One path segment from the extraction tree
 * @returns A title-cased, human-readable label
 */
function humanize(segment: string): string {
  if (/^\d+$/.test(segment)) return `#${Number(segment) + 1}`;
  const spaced = segment
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
  return spaced.replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Render a scalar extraction value as display text.
 *
 * @param value - A scalar (string / number / boolean) extraction value
 * @returns The string to show in a cell
 */
function scalarToText(value: unknown): string {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

/**
 * Recursively flatten a cleaned extraction object into leaf fields keyed by their
 * dot path. Dicts recurse; arrays index their items; scalars terminate. The first
 * path segment becomes the field's `section` (for grouping), the rest its `label`.
 *
 * @param value - The value to flatten (object, array, or scalar)
 * @param path - Accumulated path segments
 * @param out - Mutated list of flattened leaf fields
 */
function flatten(value: unknown, path: string[], out: FlatField[]): void {
  if (value === null || value === undefined) return;
  if (Array.isArray(value)) {
    value.forEach((item, index) => flatten(item, [...path, String(index)], out));
    return;
  }
  if (typeof value === 'object') {
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      flatten(child, [...path, key], out);
    }
    return;
  }
  const [head, ...rest] = path;
  out.push({
    section: humanize(head ?? ''),
    label: (rest.length ? rest : [head ?? '']).map(humanize).join(' › '),
    path: path.join('.'),
    value: scalarToText(value),
  });
}

/**
 * Whether a value is a scalar leaf (string / number / boolean / null).
 *
 * @param value - Any extraction value
 * @returns True when the value has no nested structure
 */
function isScalar(value: unknown): value is string | number | boolean | null {
  return value === null || ['string', 'number', 'boolean'].includes(typeof value);
}

/**
 * Whether a value is a "tabular" array: a non-empty array whose every element is
 * a plain object made up entirely of scalar leaves (e.g. a lab panel). These read
 * far better as a table than as flattened `#n › Field` rows.
 *
 * @param value - Any extraction value
 * @returns True when the value should be rendered as a table
 */
function isTabularArray(value: unknown): value is Record<string, unknown>[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(
      (item) =>
        item !== null &&
        typeof item === 'object' &&
        !Array.isArray(item) &&
        Object.values(item as Record<string, unknown>).every(isScalar),
    )
  );
}

/**
 * Build a {@link TableSection} from a tabular array: columns are the union of
 * item keys (first-seen order); each row maps those columns to scalar values.
 *
 * @param items - The array of scalar objects
 * @param name - Human-readable section name
 * @returns The assembled table section
 */
function buildTableSection(items: Record<string, unknown>[], name: string): TableSection {
  const columns: string[] = [];
  for (const item of items) {
    for (const key of Object.keys(item)) {
      if (!columns.includes(key)) columns.push(key);
    }
  }
  const rows = items.map((item) => {
    const row: ScalarRow = {};
    for (const col of columns) {
      const raw = item[col];
      row[col] = isScalar(raw) ? raw : '';
    }
    return row;
  });
  return { name, columns, rows };
}

/**
 * Walk an extraction object, pulling every tabular array out into `tables` and
 * returning a pruned copy with those arrays removed. The remaining structure is
 * then flattened into ordinary label/value fields.
 *
 * @param value - The value to walk
 * @param path - Accumulated path segments (last segment names extracted tables)
 * @param tables - Mutated list of extracted table sections
 * @returns The pruned value, or undefined when nothing is left
 */
function collectTables(value: unknown, path: string[], tables: TableSection[]): unknown {
  if (isTabularArray(value)) {
    const key = path[path.length - 1] ?? 'Items';
    tables.push(buildTableSection(value, humanize(key)));
    return undefined;
  }
  if (Array.isArray(value)) {
    const arr = value
      .map((item, index) => collectTables(item, [...path, String(index)], tables))
      .filter((item) => item !== undefined);
    return arr.length ? arr : undefined;
  }
  if (value && typeof value === 'object') {
    const obj: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      const pruned = collectTables(child, [...path, key], tables);
      if (pruned !== undefined) obj[key] = pruned;
    }
    return Object.keys(obj).length ? obj : undefined;
  }
  return value;
}

/**
 * Group flattened fields under their section header, keeping first-seen order.
 *
 * @param fields - The flattened leaf fields
 * @returns Sections in first-seen order, each with its rows
 */
function groupSections(fields: FlatField[]): { name: string; rows: FlatField[] }[] {
  const sections: { name: string; rows: FlatField[] }[] = [];
  const index = new Map<string, number>();
  for (const field of fields) {
    if (!index.has(field.section)) {
      index.set(field.section, sections.length);
      sections.push({ name: field.section, rows: [] });
    }
    sections[index.get(field.section)!].rows.push(field);
  }
  return sections;
}

/** Whether a table column carries a clinical status/flag (for pill coloring). */
function isStatusColumn(column: string): boolean {
  return /status|flag|abnormal/i.test(column);
}

/**
 * Map a status value (Low / High / Normal …) to a pill color, or null when the
 * value does not look like a recognized status.
 *
 * @param value - The cell text
 * @returns Background/foreground colors, or null
 */
function statusStyle(value: string): { bg: string; color: string } | null {
  const normalized = value.trim().toLowerCase();
  if (!normalized) return null;
  if (/high|elevated|critical|abnormal|positive/.test(normalized)) return { bg: '#FEE2E2', color: '#991B1B' };
  if (/low|decreased/.test(normalized)) return { bg: '#FEF3C7', color: '#92400E' };
  if (/normal|negative|within|wnl/.test(normalized)) return { bg: '#D1FAE5', color: '#065F46' };
  return null;
}

/**
 * Render a single table cell — a colored status pill for status columns, plain
 * text otherwise.
 *
 * @param props.column - The column key (used to detect status columns)
 * @param props.value - The scalar cell value
 * @returns The cell content
 */
function PanelCell({ column, value }: { column: string; value: ScalarRow[string] }) {
  const text = value === null || value === '' ? '—' : scalarToText(value);
  if (isStatusColumn(column) && text !== '—') {
    const style = statusStyle(text);
    if (style) {
      return (
        <span className="pdv-status-pill" style={{ backgroundColor: style.bg, color: style.color }}>
          {text}
        </span>
      );
    }
  }
  return <>{text}</>;
}

/**
 * Generic, schema-driven document view. Renders the LATEST document's extraction as
 * label/value sections (pulling tabular arrays out into real tables), then the
 * narrative below. For trend types (Radiology / PRO ECOG / Lab Results) the latest
 * document is the newest by date and the narrative carries the across-dates trend, so
 * the layout matches single-document (non-trend) types. Works for any document type /
 * cancer schema with no per-type mapping — new schema fields appear automatically.
 *
 * @param documents - The per-document cleaned extractions from the summary payload
 * @param narrative - Trend/comparison narrative shown below the latest document
 * @param isLoading - When true, a loading state is shown instead of the empty state
 */
export function PerDocumentDiffView({
  documents,
  narrative,
  isLoading,
  isCancer,
  excludeKeys,
  hideNarrative,
  title = 'Latest document',
}: PerDocumentDiffViewProps) {
  const docs = (documents ?? [])
    .map((doc) => ({ ...doc, data: stripKeys(doc.data, excludeKeys) }))
    .filter((doc) => doc.data && Object.keys(doc.data).length > 0);
  // Panel mode (alongside a structured view): render nothing when there are no extras,
  // rather than an empty "no extraction" card.
  if (hideNarrative && !isLoading && docs.length === 0) return null;

  if (docs.length === 0) {
    // While the summary is still being fetched, show a loader rather than the
    // empty state so the section doesn't flash "no data" before results arrive.
    if (isLoading) {
      return (
        <div className="pdv-report">
          <div className="pdv-report-header">
            <div className="pdv-report-title">
              <FileText className="pdv-report-icon" />
              <span className="pdv-report-title-text">Report summary</span>
            </div>
          </div>
          <div className="pdv-narrative-box" role="status" aria-live="polite">
            <SpinningLoader />
          </div>
        </div>
      );
    }

    return (
      <div className="pdv-report">
        <div className="pdv-report-header">
          <div className="pdv-report-title">
            <FileText className="pdv-report-icon" />
            <span className="pdv-report-title-text">Report summary</span>
          </div>
        </div>
        <div className="pdv-narrative-box">
          {narrative ? (
            <Markdown className="pdv-narrative-text">{narrative}</Markdown>
          ) : isCancer === false ? (
            <p className="pdv-narrative-text">
              This patient has no cancer diagnosis on record, so this report is not AI-summarised. Use
              <strong> View Source</strong> above to open the original report.
            </p>
          ) : (
            <p className="pdv-narrative-text">No structured extraction available for this document type.</p>
          )}
        </div>
      </div>
    );
  }

  // Documents arrive per date; render the LATEST (newest) one in full. The narrative
  // below carries the across-dates trend for trend types, so both trend and non-trend
  // read the same: latest document's fields on top, comparison/trend narrative under it.
  const latest = [...docs].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).at(-1)!;

  // Pull tabular arrays (lab panels, medication lists, …) out into real tables, then
  // flatten only the remaining scalar/nested fields into label/value sections so
  // nothing renders as `#n › Field` noise.
  const panelTables: TableSection[] = [];
  const pruned = collectTables(latest.data, [], panelTables) ?? {};
  const flat: FlatField[] = [];
  flatten(pruned, [], flat);
  const singleByPath = new Map(flat.map((field) => [field.path, field]));
  const scalarSections = groupSections(flat);

  return (
    <div className="pdv-report">
      <div className="pdv-report-header">
        <div className="pdv-report-title">
          <FileText className="pdv-report-icon" />
          <span className="pdv-report-title-text">{title}</span>
          <span className="pdv-report-subtitle">{formatDate(latest.date)}</span>
        </div>
      </div>

      <div className="pdv-sections">
        {scalarSections.map((section) => {
          const isSingle = section.rows.length === 1 && section.rows[0].label === section.name;
          const hasLongValue = section.rows.some((row) => (singleByPath.get(row.path)?.value ?? '').length > 120);
          return (
            <div
              key={`sec-${section.name}`}
              className={`pdv-section-box${hasLongValue ? ' pdv-section-box--wide' : ''}`}
            >
              <div className="pdv-section-box-title">{section.name}</div>
              {isSingle ? (
                <div className="pdv-section-single-value">{singleByPath.get(section.rows[0].path)?.value || '—'}</div>
              ) : (
                <dl className="pdv-fields">
                  {section.rows.map((row) => (
                    <div key={row.path} className="pdv-field">
                      <dt className="pdv-field-label">{row.label}</dt>
                      <dd className="pdv-field-value">{singleByPath.get(row.path)?.value || '—'}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          );
        })}

        {/* Tabular arrays (lab panels, med lists, …) rendered as real tables. */}
        {panelTables.map((table) => (
          <div key={`tbl-${table.name}`} className="pdv-section-box pdv-section-box--wide pdv-panel">
            <div className="pdv-section-box-title">{table.name}</div>
            <div className="pdv-panel-scroll">
              <table className="pdv-panel-table">
                <thead>
                  <tr>
                    {table.columns.map((column) => (
                      <th key={column}>{humanize(column)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {table.columns.map((column) => (
                        <td key={column}>
                          <PanelCell column={column} value={row[column]} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      {narrative && !hideNarrative && (
        <div className="pdv-section pdv-section-with-separator">
          <h3 className="pdv-section-title">Narrative summary</h3>
          <div className="pdv-narrative-box">
            <Markdown className="pdv-narrative-text">{narrative}</Markdown>
          </div>
        </div>
      )}
    </div>
  );
}
